import { Card, Stack, Typography } from '@mui/material'
import type { IProjectNeedSlotResource, TDictionaryItem } from '../../../../api/types'

const resourceAcquisitionLabels: Record<string, string> = {
  gift: 'Получить безвозмездно',
  temporary_use: 'Временно получить',
  rent: 'Арендовать',
  buy: 'Купить',
  barter: 'Обмен/бартер',
  shared_use: 'Совместно использовать',
  access: 'Доступ без передачи',
}

const resourceDurationLabels: Record<string, string> = {
  once: 'Разово',
  period: 'На период',
  permanent: 'Постоянно',
}

const resourceDeliveryLabels: Record<string, string> = {
  delivery: 'Доставка',
  pickup: 'Самовывоз',
  on_site: 'Использовать на месте',
  remote: 'Удалённо',
  discussable: 'Обсуждается',
}

const resourceBudgetLabels: Record<string, string> = {
  free: 'Бесплатно',
  has_budget: 'Есть бюджет',
  discussable: 'Обсуждается',
  barter: 'Бартер',
}

const dictionaryNames = (items: TDictionaryItem[]) => items.map((item) => item.name).filter(Boolean)

function dictionaryGroups(slot: IProjectNeedSlotResource): { label: string; names: string[] }[] {
  const groups = slot.kind === 'role'
    ? [
      { label: 'Компетенции', names: dictionaryNames(slot.competencies) },
      { label: 'Условия участия', names: dictionaryNames(slot.participation) },
    ]
    : [
      { label: 'Ресурсы', names: dictionaryNames(slot.resources) },
    ]

  return groups.filter((group) => group.names.length > 0)
}

function roleDetails(slot: IProjectNeedSlotResource): string[] {
  return [slot.city, slot.region, slot.remote_allowed ? 'Можно удалённо' : null]
    .filter((item): item is string => Boolean(item))
}

function resourceDetails(slot: IProjectNeedSlotResource): string[] {
  const meta = slot.meta ?? {}

  return [
    meta.quantity ? `Количество: ${meta.quantity}` : null,
    meta.acquisition_type ? resourceAcquisitionLabels[meta.acquisition_type] ?? meta.acquisition_type : null,
    meta.duration_type ? resourceDurationLabels[meta.duration_type] ?? meta.duration_type : null,
    meta.delivery_type ? resourceDeliveryLabels[meta.delivery_type] ?? meta.delivery_type : null,
    meta.budget_type ? `Бюджет: ${resourceBudgetLabels[meta.budget_type] ?? meta.budget_type}` : null,
    meta.budget_comment ? `Комментарий: ${meta.budget_comment}` : null,
    slot.city || slot.region ? [slot.city, slot.region].filter(Boolean).join(', ') : null,
  ].filter((item): item is string => Boolean(item))
}

export function NeedSlotCard({ slot }: { slot: IProjectNeedSlotResource }) {
  const groups = dictionaryGroups(slot)
  const details = slot.kind === 'resource' ? resourceDetails(slot) : roleDetails(slot)

  return (
    <Card variant="primaryAdaptiveCard" sx={{ overflowWrap: 'anywhere' }}>
      <Stack gap={1}>
        <Stack gap={0.5}>
          <Typography variant="black-16-semiBold" component="div">
            {slot.title}
          </Typography>
          {slot.description && (
            <Typography variant="body1">
              {slot.description}
            </Typography>
          )}
        </Stack>
        {groups.length > 0 && (
          <Stack gap={0.75}>
            {groups.map((group) => (
              <Typography key={group.label} variant="body2" component="div">
                <Typography component="span" variant="body2" color="text.secondary">
                  {group.label}:{' '}
                </Typography>
                <Typography component="span" variant="black-14-medium">
                  {group.names.join('; ')}
                </Typography>
              </Typography>
            ))}
          </Stack>
        )}
        {slot.kind === 'resource' && slot.meta?.requirements && (
          <Typography variant="body2" color="text.secondary">
            {slot.meta.requirements}
          </Typography>
        )}
        {details.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            {details.join(' · ')}
          </Typography>
        )}
      </Stack>
    </Card>
  )
}
